// src/components/timeline/weekEvents/useWeeklyBuckets.ts
import { useMemo } from 'react';
import { CalendarEvent } from '../hooks/useEvents';
import { AvailabilityObject } from '../types/schemas/abc';
import { OverlayEvent } from '../types/schemas/calendar';
import { adaptApiEvents, generateFreeSlots, getMondayBasedIndex, mergeFreeBlocks, splitOvernightEvents, dayIndex, fractionalHour } from '../utils/calendarGrid';
import { fromZonedTime } from 'date-fns-tz';

export const useWeeklyBuckets = (
  weekStart: Date,
  availability: AvailabilityObject | null,
  events?: CalendarEvent[] | null,
  activeTz?: string,
  homeTz?: string,
) => {
  return useMemo(() => {
    // Build week boundaries in activeTz so filtering matches the displayed week
    let weekStartUtc: Date;
    let weekEndUtc: Date;
    if (activeTz) {
      // weekStart is browser-local Monday 00:00. Convert to activeTz's Monday 00:00.
      const y = weekStart.getFullYear();
      const m = weekStart.getMonth();
      const d = weekStart.getDate();
      weekStartUtc = fromZonedTime(new Date(y, m, d, 0, 0, 0, 0), activeTz);
      weekEndUtc = fromZonedTime(new Date(y, m, d + 7, 0, 0, 0, 0), activeTz);
    } else {
      weekStartUtc = weekStart;
      weekEndUtc = new Date(+weekStart + 7 * 864e5);
    }

    // 1. Prepare raw lists
    const apiEvents = adaptApiEvents(events ?? [], activeTz);
    const freeSlots = generateFreeSlots(availability, weekStart, homeTz);

    // 2. Filter API events to current week (using tz-aware boundaries)
    const relevantApiEvents = apiEvents.filter(ev =>
      new Date(ev.start) < weekEndUtc && new Date(ev.end) > weekStartUtc
    );

    // 3. Merge Free blocks
    const mergedEvents = mergeFreeBlocks([...relevantApiEvents, ...freeSlots]);

    const splitEvents = splitOvernightEvents(mergedEvents, activeTz);

    // 4. Sort into Daily Buckets
    const buckets: OverlayEvent[][] = Array.from({ length: 7 }, () => []);

    splitEvents.forEach(ev => {
      const dayIdx = getMondayBasedIndex(ev.start, activeTz);
      if (buckets[dayIdx]) {
        buckets[dayIdx].push(ev);
      }
    });

    // 5. Sort each bucket by time
    buckets.forEach(list =>
      list.sort((a, b) => +new Date(a.start) - +new Date(b.start))
    );

    return buckets;
  }, [weekStart, availability, events, activeTz, homeTz]);
};
